<template>
  <PlatformLayout
    :layout-class="$store.state.role === 'supervisor' ? 'mainStyle3' : 'mainStyle1'"
    title="Международные гранты"
  >
    <div class="gp">
      <!-- Toolbar: filters + view toggle + count -->
      <div class="gp-toolbar">
        <div class="gp-toolbar__filters">
          <button
            type="button"
            class="gp-chip"
            :class="{ 'gp-chip--active': !selectedDegree }"
            @click="selectedDegree = null"
          >Все</button>
          <button
            v-for="deg in studyDegrees"
            :key="deg.id"
            type="button"
            class="gp-chip"
            :class="{ 'gp-chip--active': selectedDegree === deg.id }"
            @click="selectedDegree = deg.id"
          >{{ deg.title }}</button>
        </div>
        <div class="gp-toolbar__right">
          <span class="gp-toolbar__count" v-if="!loading">
            {{ filteredGrants.length }} {{ grantsCountLabel }}
          </span>
          <div class="gp-view-toggle" role="group" aria-label="Вид отображения">
            <button
              type="button"
              class="gp-view-btn"
              :class="{ 'gp-view-btn--active': viewMode === 'grid' }"
              @click="viewMode = 'grid'"
              title="Сетка"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            </button>
            <button
              type="button"
              class="gp-view-btn"
              :class="{ 'gp-view-btn--active': viewMode === 'list' }"
              @click="viewMode = 'list'"
              title="Список"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                <circle cx="3" cy="6" r="1" fill="currentColor" stroke="none"/>
                <circle cx="3" cy="12" r="1" fill="currentColor" stroke="none"/>
                <circle cx="3" cy="18" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" :class="viewMode === 'grid' ? 'gp-grid' : 'gp-list'">
        <div v-for="n in (viewMode === 'grid' ? 6 : 5)" :key="n" class="gp-skeleton" :class="'gp-skeleton--' + viewMode" />
      </div>

      <!-- Cards -->
      <div v-else-if="filteredGrants.length" :class="viewMode === 'grid' ? 'gp-grid' : 'gp-list'">
        <intGrantCard
          v-for="item in grantsToShow"
          :key="item.id"
          :item="item"
          :mode="viewMode"
        />
      </div>

      <!-- Empty -->
      <div v-else class="gp-empty">
        <div class="gp-empty__icon">🔍</div>
        <p class="gp-empty__title">Гранты не найдены</p>
        <p class="gp-empty__text">Попробуйте изменить фильтр</p>
      </div>

      <!-- Show more -->
      <button
        v-if="!loading && filteredGrants.length > visibleCount"
        type="button"
        class="gp-more"
        @click="showAll = !showAll"
      >
        {{ showAll ? 'Свернуть' : `Показать все (${filteredGrants.length})` }}
      </button>
    </div>
  </PlatformLayout>
</template>

<script>
import PlatformLayout from '@/components/platform/PlatformLayout.vue';
import intGrantCard from '@/components/intGrantCard.vue';
import axios from 'axios';

export default {
  components: { PlatformLayout, intGrantCard },
  data() {
    return {
      grants: [],
      studyDegrees: [],
      selectedDegree: null,
      showAll: false,
      visibleCount: 12,
      loading: true,
      viewMode: 'grid',
    };
  },
  computed: {
    filteredGrants() {
      if (!this.selectedDegree) return this.grants;
      return this.grants.filter((g) => g.study_degree_id === this.selectedDegree);
    },
    grantsToShow() {
      return this.showAll ? this.filteredGrants : this.filteredGrants.slice(0, this.visibleCount);
    },
    grantsCountLabel() {
      const n = this.filteredGrants.length;
      const m10 = n % 10;
      const m100 = n % 100;
      if (m10 === 1 && m100 !== 11) return 'грант';
      if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'гранта';
      return 'грантов';
    },
  },
  watch: {
    selectedDegree() {
      this.showAll = false;
      this.fetchGrants();
    },
  },
  methods: {
    async fetchGrants() {
      try {
        this.loading = true;
        const params = {};
        if (this.selectedDegree) params.study_degree_id = this.selectedDegree;
        const { data } = await axios.get('/platform/scholarships', { params });
        this.grants = data.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async fetchFilters() {
      try {
        const { data } = await axios.get('/platform/study-degrees');
        this.studyDegrees = data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
  },
  async created() {
    await this.fetchFilters();
    await this.fetchGrants();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/grants';

$gp-font: 'Manrope', sans-serif;

.gp {
  font-family: $gp-font;
  -webkit-font-smoothing: antialiased;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── Toolbar ───────────────────────────────────────── */
.gp-toolbar {
  display: flex; align-items: center;
  justify-content: space-between; gap: 16px; flex-wrap: wrap;

  &__filters {
    display: flex; flex-wrap: wrap; gap: 8px;
  }

  &__right {
    display: flex; align-items: center; gap: 16px; flex-shrink: 0;
  }

  &__count {
    font-size: 13px; font-weight: 600; color: #9ca3af; white-space: nowrap;
  }
}

.gp-chip {
  padding: 7px 16px; border-radius: 999px;
  border: 1px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13px; font-weight: 600;
  font-family: inherit; cursor: pointer;
  transition: border-color .14s, color .14s, background .14s;

  &:hover { border-color: #d1d5db; color: #111827; }
  &--active { border-color: #e8192c; background: rgba(232,25,44,.06); color: #e8192c; }
}

/* ── View toggle ───────────────────────────────────── */
.gp-view-toggle {
  display: flex; border-radius: 10px;
  border: 1px solid #e5e7eb; overflow: hidden; background: #fff;
}

.gp-view-btn {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 34px; border: none; background: transparent;
  color: #9ca3af; cursor: pointer;
  transition: background .14s, color .14s;

  &:hover { background: #f3f4f6; color: #374151; }
  &--active { background: #111827; color: #fff; }

  &:first-child { border-right: 1px solid #e5e7eb; }
}

/* ── Grid layout ───────────────────────────────────── */
.gp-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 1280px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: 1fr; gap: 12px; }
}

/* ── List layout ───────────────────────────────────── */
.gp-list {
  display: flex; flex-direction: column; gap: 10px;
}

/* ── Skeletons ─────────────────────────────────────── */
.gp-skeleton {
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8eaed 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: gp-shimmer 1.2s ease-in-out infinite;

  &--grid { height: 220px; }
  &--list { height: 88px; }
}

@keyframes gp-shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* ── Empty ─────────────────────────────────────────── */
.gp-empty {
  text-align: center; padding: 56px 24px;
  background: #f8f9fb; border-radius: 16px;
  border: 1px dashed #e5e7eb;

  &__icon { font-size: 36px; margin-bottom: 12px; }
  &__title { margin: 0 0 6px; font-size: 16px; font-weight: 700; color: #111827; }
  &__text  { margin: 0; font-size: 14px; color: #9ca3af; }
}

/* ── More ──────────────────────────────────────────── */
.gp-more {
  align-self: center;
  padding: 11px 28px; border-radius: 10px;
  border: 1px solid #e5e7eb; background: #fff;
  color: #374151; font-size: 14px; font-weight: 700;
  font-family: $gp-font; cursor: pointer;
  transition: border-color .14s, background .14s;

  &:hover { border-color: #d1d5db; background: #f9fafb; }
}
</style>
